import { MdClose } from 'react-icons/md';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { Appearance, Size } from '@Components/designConstants';
import { LabelPosition, ToggleSwitch } from '@Components/ToggleSwitch';
import { Platform, indexedVisibleRows, updateAt } from './streaming-networks-constants';
import {
  applyPlatformSelection,
  selectedPlatformNames,
  trimmedPlatformName
} from './streaming-networks-utils';

interface PlatformSelectProps {
  platforms: Platform[];
  options: string[];
  editing: boolean;
  onChange: (platforms: Platform[]) => void;
}

interface PlatformChipProps {
  row: Platform;
  editing: boolean;
  removable: boolean;
  onBuyableChange: (buyable: boolean) => void;
  onRemove: () => void;
}

const PlatformChip = ({
  row,
  editing,
  removable,
  onBuyableChange,
  onRemove
}: PlatformChipProps) => {
  const name = trimmedPlatformName(row);
  return (
    <div className='sn-platform-chip'>
      <span className='sn-platform-name'>{name}</span>
      <ToggleSwitch
        label='Buyable'
        labelPosition={LabelPosition.TRAILING}
        appearance={Appearance.EMPTY}
        size={Size.SM}
        checked={row.buyable}
        disabled={!editing}
        onChange={next => onBuyableChange(typeof next === 'function' ? next(row.buyable) : next)}
      />
      {editing && removable && (
        <IconButton size={Size.SM} aria-label={`Remove ${name}`} onClick={onRemove}>
          <MdClose />
        </IconButton>
      )}
    </div>
  );
};

// Multi-select of platform names. Each selected platform is a chip carrying its Buyable switch and
// a remove button.
export const PlatformSelect = ({ platforms, options, editing, onChange }: PlatformSelectProps) => {
  const names = selectedPlatformNames(platforms);

  return (
    <div className='sn-platform-list'>
      <div className='sn-platform-dropdown'>
        <Dropdown<string>
          searchable
          multiSelect
          options={options.map(platform => ({ label: platform, value: platform }))}
          value={names}
          placeholder={editing ? 'Select platforms…' : 'None'}
          disabled={!editing}
          onChange={selected => onChange(applyPlatformSelection(platforms, selected))}
        />
      </div>
      <div className='sn-platform-chips'>
        {indexedVisibleRows(platforms)
          .filter(({ row }) => trimmedPlatformName(row))
          .map(({ row, originalIndex }) => {
            const name = trimmedPlatformName(row);
            return (
              <PlatformChip
                key={originalIndex}
                row={row}
                editing={editing}
                removable={names.includes(name)}
                onBuyableChange={buyable =>
                  onChange(updateAt(platforms, originalIndex, { ...row, buyable }))
                }
                onRemove={() =>
                  onChange(
                    applyPlatformSelection(
                      platforms,
                      names.filter(n => n !== name)
                    )
                  )
                }
              />
            );
          })}
      </div>
    </div>
  );
};
